import { useAuthenticator } from "@aws-amplify/ui-react";
import {
  ArrowRightStartOnRectangleIcon,
  ArrowUpTrayIcon,
  BookOpenIcon,
  ChatBubbleLeftRightIcon,
  CircleStackIcon,
  Cog6ToothIcon,
  CommandLineIcon,
  HomeIcon,
  PlusIcon,
  PuzzlePieceIcon,
  QuestionMarkCircleIcon,
  RectangleGroupIcon,
  UserGroupIcon,
} from "@heroicons/react/24/outline";
import { useStore } from "@nanostores/react";
import { getPagePath } from "@nanostores/router";
import clsx from "clsx";
import { ReactElement } from "react";

import LogoIcon from "../assets/icons/logo.svg";
import { $modifiableGraphMeta } from "../stores/graph/metadata.ts";
import { $router } from "../stores/router.ts";
import { Button } from "./catalyst/Button.tsx";
import { SidebarDivider } from "./catalyst/SidebarDivider";
import { SidebarSection } from "./catalyst/SidebarSection";
import { CollapsibleSidebar } from "./CollapsibleSidebar";
import {
  CollapsibleSidebarItem,
  CollapsibleSidebarLabel,
} from "./CollapsibleSidebarItem";
import SideBarActionButton from "./SideBarActionButton.tsx";
import { SidebarGroup } from "./SidebarGroup";

const COLLAPSE_STORAGE_KEY = "mvz.sidebar.collapsed";

export const SidebarMenu = (): ReactElement => {
  const page = useStore($router);
  const auth = useAuthenticator((context) => [context.user, context.signOut]);

  const route = page?.route;
  const isWorkspaces = route === "index" || route === "graph";
  const isEntities =
    route === "entities" ||
    route === "entityClassCreate" ||
    route === "entityClassEdit" ||
    route === "entityCreate" ||
    route === "entityEdit";
  const isIntegrations =
    route === "integrations" ||
    route === "integrationsCreate" ||
    route === "integrationsEdit" ||
    route === "newIntegrations" ||
    route === "queryActionNew" ||
    route === "queryActionEdit" ||
    route === "queryActionDelete";
  const isDestinations =
    route === "destinations" ||
    route === "destinationsCreate" ||
    route === "destinationsEdit" ||
    route === "newDestinations";
  const isAgents =
    route === "alertSources" ||
    route === "alertSourcesCreate" ||
    route === "alertSourcesEdit";

  const handleWorkspacesAdd = (): void => {
    $modifiableGraphMeta.set({ type: "create" });
  };

  return (
    <CollapsibleSidebar
      icon={<LogoIcon className="h-8 w-8" />}
      storageKey={COLLAPSE_STORAGE_KEY}
      title="Miru"
    >
      <CollapsibleSidebarItem
        current={route === "dashboard"}
        href={getPagePath($router, "dashboard")}
        label="Home"
      >
        <HomeIcon data-slot="icon" />
        <CollapsibleSidebarLabel>Home</CollapsibleSidebarLabel>
      </CollapsibleSidebarItem>

      <CollapsibleSidebarItem
        current={route === "alerts" || route === "alertDetails"}
        href={getPagePath($router, "alerts")}
        label="Processes"
      >
        <CommandLineIcon data-slot="icon" />
        <CollapsibleSidebarLabel>Processes</CollapsibleSidebarLabel>
      </CollapsibleSidebarItem>

      <CollapsibleSidebarItem
        current={route === "chat"}
        href={getPagePath($router, "chat")}
        label="Chat"
      >
        <ChatBubbleLeftRightIcon data-slot="icon" />
        <CollapsibleSidebarLabel>Chat</CollapsibleSidebarLabel>
      </CollapsibleSidebarItem>

      <CollapsibleSidebarItem
        button={
          <Button
            aria-label="Add new workspace"
            className={clsx(
              "shrink-0 rounded-lg p-1.5",
              "data-[slot=icon]:*:size-4 data-[slot=icon]:*:fill-zinc-500",
              "dark:data-[slot=icon]:*:fill-zinc-400",
            )}
            onClick={handleWorkspacesAdd}
            plain
            size="xs"
          >
            <PlusIcon data-slot="icon" />
          </Button>
        }
        current={isWorkspaces}
        href={getPagePath($router, "index")}
        label="Workspaces"
      >
        <RectangleGroupIcon data-slot="icon" />
        <CollapsibleSidebarLabel>Workspaces</CollapsibleSidebarLabel>
      </CollapsibleSidebarItem>

      <SidebarDivider />

      <SidebarGroup
        icon={<Cog6ToothIcon data-slot="icon" />}
        label="Configure"
        storageKey="mvz.sidebar.group.configure"
      >
        <div className="space-y-0.5">
          <CollapsibleSidebarItem
            button={
              <SideBarActionButton
                href={getPagePath($router, "alertSourcesCreate")}
                label="Add new agent"
              />
            }
            current={isAgents}
            href={getPagePath($router, "alertSources")}
            label="Agents"
          >
            <UserGroupIcon data-slot="icon" />
            <CollapsibleSidebarLabel>Agents</CollapsibleSidebarLabel>
          </CollapsibleSidebarItem>
          <CollapsibleSidebarItem
            current={isEntities}
            href={getPagePath($router, "entities")}
            label="Entities"
          >
            <CircleStackIcon data-slot="icon" />
            <CollapsibleSidebarLabel>Entities</CollapsibleSidebarLabel>
          </CollapsibleSidebarItem>
          <CollapsibleSidebarItem
            current={isIntegrations}
            href={getPagePath($router, "integrations")}
            label="Integrations"
          >
            <PuzzlePieceIcon data-slot="icon" />
            <CollapsibleSidebarLabel>Integrations</CollapsibleSidebarLabel>
          </CollapsibleSidebarItem>
          <CollapsibleSidebarItem
            current={
              route === "knowledgeBase" || route === "knowledgeBaseDetail"
            }
            href={getPagePath($router, "knowledgeBase")}
            label="Knowledge Base"
          >
            <BookOpenIcon data-slot="icon" />
            <CollapsibleSidebarLabel>Knowledge Base</CollapsibleSidebarLabel>
          </CollapsibleSidebarItem>
          <CollapsibleSidebarItem
            current={isDestinations}
            href={getPagePath($router, "destinations")}
            label="Destinations"
          >
            <ArrowUpTrayIcon data-slot="icon" />
            <CollapsibleSidebarLabel>Destinations</CollapsibleSidebarLabel>
          </CollapsibleSidebarItem>
        </div>
      </SidebarGroup>

      <SidebarDivider />

      <CollapsibleSidebarItem
        href="https://miru-labs.gitbook.io/miru/y0ukxYqBXl34AjvGB60x/"
        label="Help & Resources"
        rel="noopener noreferrer"
        target="_blank"
      >
        <QuestionMarkCircleIcon data-slot="icon" />
        <CollapsibleSidebarLabel>Help &amp; Resources</CollapsibleSidebarLabel>
      </CollapsibleSidebarItem>

      {auth.user && (
        <SidebarSection className="mt-auto">
          <CollapsibleSidebarItem label="Sign out" onClick={auth.signOut}>
            <ArrowRightStartOnRectangleIcon data-slot="icon" />
            <CollapsibleSidebarLabel>Sign out</CollapsibleSidebarLabel>
          </CollapsibleSidebarItem>
        </SidebarSection>
      )}
    </CollapsibleSidebar>
  );
};
